import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Calendar as CalIcon } from "lucide-react";
import {
  COURSES,
  BRAND,
  JUNIOR_PROGRAM,
  PERMIT_ADDONS,
  PERMIT_ADDON_PRICING,
} from "../data/staticData";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import PermitAddOnSelector from "../components/PermitAddOnSelector";
import { useAddOns } from "../components/AddOnsContext";

const AVAILABLE_ADDON_CODES = new Set(
  PERMIT_ADDONS.filter((a) => a.available).map((a) => a.code)
);

function parseAddonsParam(raw) {
  if (!raw) return [];
  return raw
    .split(",")
    .map((c) => c.trim().toUpperCase())
    .filter((c) => AVAILABLE_ADDON_CODES.has(c));
}

const COURSE_OPTIONS = [
  { value: "", label: "Select a course" },
  ...COURSES.map((c) => ({ value: c.name, label: c.name })),
  {
    value: JUNIOR_PROGRAM.bookingValue,
    label: `${JUNIOR_PROGRAM.name} ($${JUNIOR_PROGRAM.price})`,
  },
  { value: "Private 1-on-1", label: "Private 1-on-1" },
  { value: "Group / Custom", label: "Group / Custom" },
];

const EXPERIENCE_OPTIONS = [
  { value: "", label: "Select level" },
  { value: "Brand New", label: "Brand New" },
  { value: "Some Experience", label: "Some Experience" },
  { value: "Concealed Carrier", label: "Concealed Carrier" },
  { value: "Advanced", label: "Advanced" },
];

const NEXT_STEPS = [
  "We confirm availability within 24 hours via phone or email.",
  "You'll receive a secure payment link and pre-class checklist.",
  "Show up ready to learn — we handle the rest.",
];


export default function Booking() {
  const [params] = useSearchParams();
  const preCourse = params.get("course") || "";
  const {
    selected: selectedAddons,
    toggle: toggleAddon,
    setAll: setAllAddons,
    clear: clearAddons,
    pricing: addonPricing,
  } = useAddOns();

  // Hydrate add-ons from URL (e.g. ?addons=MD,FL) once on mount
  useEffect(() => {
    const fromUrl = parseAddonsParam(params.get("addons"));
    if (fromUrl.length > 0) setAllAddons(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    course: preCourse,
    preferred_date: "",
    experience_level: "",
    comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        addons: selectedAddons,
        addon_price: addonPricing.total,
      });
      setDone(true);
      toast.success("Booking submitted. We'll be in touch within 24 hours.");
      // Clear add-ons after successful submission so the indicator resets
      clearAddons();
    } catch (err) {
      toast.error("Couldn't submit booking. Try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="container-fld py-32" data-testid="booking-success">
        <div className="max-w-2xl mx-auto text-center tactical-card p-12 relative">
          <CheckCircle2 className="w-16 h-16 text-[#7CFF00] mx-auto mb-6" />
          <div className="overline mb-3">// Booking Received</div>
          <h1 className="font-tactical text-4xl md:text-5xl font-bold uppercase text-white tracking-tight">
            Stand By. We&apos;ve Got You.
          </h1>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Thanks, <span className="text-white">{form.name.split(" ")[0]}</span>
            . We received your request for{" "}
            <span className="text-[#7CFF00]">{form.course || "training"}</span>{" "}
            and will reach out within 24 hours to confirm date, location, and
            payment.
          </p>
          {selectedAddons.length > 0 && (
            <div
              data-testid="booking-success-addons"
              className="mt-6 inline-flex flex-col gap-2 items-center"
            >
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="overline text-[10px]">// Add-Ons Reserved</span>
                {selectedAddons.map((code) => (
                  <span
                    key={code}
                    className="font-mono-tactical text-[10px] tracking-widest uppercase px-2 py-1 border border-[#7CFF00] text-[#7CFF00] bg-[#7CFF00]/10"
                  >
                    {code}
                  </span>
                ))}
              </div>
              <span className="font-tactical text-[#7CFF00] text-lg">
                +${addonPricing.total}
                {addonPricing.isBundle && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest">
                    (Bundle • Save ${PERMIT_ADDON_PRICING.bundleSavings})
                  </span>
                )}
              </span>
            </div>
          )}
          <p className="mt-4 text-zinc-500 text-sm font-mono-tactical">
            Need us sooner? Call {BRAND.phone}.
          </p>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="page-booking">
      <section className="container-fld py-20">
        <div className="overline mb-4">// Book Training</div>
        <h1 className="font-tactical text-5xl md:text-7xl font-bold uppercase text-white tracking-tight leading-none max-w-4xl">
          Reserve Your <span className="text-[#7CFF00]">Spot.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-zinc-400 text-lg leading-relaxed">
          Fill out the form below and we&apos;ll confirm your training session
          within 24 hours. All bookings are subject to availability and final
          payment via secure invoice.
        </p>
      </section>

      <section className="container-fld pb-24 grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="tactical-card p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <Field
              label="Full Name"
              testid="booking-name"
              value={form.name}
              onChange={update("name")}
              required
            />
            <Field
              label="Phone"
              type="tel"
              testid="booking-phone"
              value={form.phone}
              onChange={update("phone")}
              required
            />
            <Field
              label="Email"
              type="email"
              testid="booking-email"
              value={form.email}
              onChange={update("email")}
              required
              full
            />
            <SelectField
              label="Desired Course"
              testid="booking-course"
              value={form.course}
              onChange={update("course")}
              options={COURSE_OPTIONS}
              required
            />
            <Field
              label="Preferred Date"
              type="date"
              testid="booking-date"
              value={form.preferred_date}
              onChange={update("preferred_date")}
              required
            />
            <SelectField
              label="Experience Level"
              testid="booking-experience"
              value={form.experience_level}
              onChange={update("experience_level")}
              options={EXPERIENCE_OPTIONS}
              required
              full
            />
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Questions / Comments</label>
              <textarea
                value={form.comments}
                onChange={update("comments")}
                rows={4}
                data-testid="booking-comments"
                className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none transition-colors"
                placeholder="Anything we should know? Goals, group size, prior training..."
              />
            </div>

            {/* Embedded add-on selector */}
            <div className="sm:col-span-2 -mx-1">
              <div className="overline mb-3">// Multi-State Add-On Upgrades</div>
              <PermitAddOnSelector
                selected={selectedAddons}
                onToggle={toggleAddon}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="btn-primary sm:col-span-2 justify-center disabled:opacity-50"
            >
              <CalIcon className="w-4 h-4" />
              {submitting ? "Submitting..." : "Reserve Your Spot"}
            </button>
            <p className="sm:col-span-2 text-xs text-zinc-500 font-mono-tactical text-center">
              By submitting, you agree to be contacted by Frontline Defense LLC
              regarding your booking.
            </p>
          </form>
        </Reveal>

        <Reveal delay={150} className="md:col-span-5 space-y-6">
          {/* Live reservation summary */}
          <div
            data-testid="booking-reservation-summary"
            className="tactical-card p-6"
          >
            <div className="overline mb-3">// Reservation Summary</div>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-zinc-400 text-sm">Course / Program</span>
                <span className="font-tactical text-white text-sm uppercase tracking-wider text-right">
                  {form.course || "Not selected"}
                </span>
              </div>
              <div className="border-t border-white/10 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-400 text-sm">Add-On States</span>
                  <span className="font-mono-tactical text-[10px] uppercase tracking-widest text-zinc-500">
                    {selectedAddons.length}{" "}
                    {selectedAddons.length === 1 ? "state" : "states"}
                  </span>
                </div>
                {selectedAddons.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedAddons.map((code) => (
                      <span
                        key={code}
                        data-testid={`summary-addon-${code.toLowerCase()}`}
                        className="font-mono-tactical text-[10px] tracking-widest uppercase px-2 py-1 border border-[#7CFF00] text-[#7CFF00] bg-[#7CFF00]/10"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 font-mono-tactical italic">
                    No add-ons selected
                  </p>
                )}
              </div>
              {selectedAddons.length > 0 && (
                <div className="border-t border-white/10 pt-3 flex items-baseline justify-between">
                  <span className="overline text-[10px]">
                    {addonPricing.isBundle
                      ? `${addonPricing.count}-State Bundle`
                      : "Add-On Total"}
                  </span>
                  <span
                    data-testid="summary-addon-total"
                    className="font-tactical text-2xl text-[#7CFF00] font-bold glow-text"
                  >
                    +${addonPricing.total}
                  </span>
                </div>
              )}
              {addonPricing.isBundle && (
                <p className="text-[10px] font-mono-tactical uppercase tracking-widest text-[#7CFF00]">
                  // Bundle savings applied — Save ${PERMIT_ADDON_PRICING.bundleSavings}
                </p>
              )}
            </div>
          </div>

          <div className="tactical-card p-6">
            <div className="overline mb-3">// What Happens Next</div>
            <ol className="space-y-4 text-zinc-300 text-sm">
              {NEXT_STEPS.map((step, i) => (
                <li key={step} className="flex gap-4">
                  <span className="font-tactical text-[#7CFF00] text-xl leading-none">
                    0{i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="tactical-card p-6">
            <div className="overline mb-3">// Direct Line</div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Prefer to talk it through? Call us directly. Real veteran on the
              other end — no call centers.
            </p>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="font-tactical text-2xl text-[#7CFF00] hover:underline mt-4 block"
              data-testid="booking-phone-cta"
            >
              {BRAND.phone}
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function Field({ label, testid, full, ...rest }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="overline block mb-2">{label}</label>
      <input
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none transition-colors"
        {...rest}
      />
    </div>
  );
}

function SelectField({ label, testid, options, full, ...rest }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="overline block mb-2">{label}</label>
      <select
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none transition-colors"
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#0A0A0A]">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
